/**
 * Sentry Error Tracking
 * =====================
 * Production-only error tracking with subsystem tagging.
 * Disabled in dev to avoid noise.
 */

/* global __APP_VERSION__ */
import * as Sentry from '@sentry/react';

/**
 * Initialize Sentry SDK. Call BEFORE ReactDOM.createRoot().
 */
export function initSentry() {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    enabled: import.meta.env.PROD,
    release: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : undefined,

    // Performance: low sample rate for free tier
    tracesSampleRate: 0.1,

    // Session Replay: only on errors
    replaysOnErrorSampleRate: 1.0,
    replaysSessionSampleRate: 0,

    integrations: [
      Sentry.replayIntegration({
        blockAllMedia: false,
      }),
    ],

    // Filter noise
    ignoreErrors: [
      'ResizeObserver loop',
      'Non-Error promise rejection',
      /Loading chunk .* failed/,
    ],

    // Strip FAL API keys from error payloads
    beforeSend(event) {
      if (event.message) {
        event.message = event.message.replace(/Key\s+[A-Za-z0-9_-]+/gi, 'Key [REDACTED]');
      }
      return event;
    },
  });
}

/**
 * Set or clear Sentry user context from Supabase auth.
 */
export function setSentryUser(user) {
  if (user) {
    Sentry.setUser({ id: user.id, email: user.email });
  } else {
    Sentry.setUser(null);
  }
}

/**
 * Capture an error scoped to a specific subsystem.
 * Use in catch blocks for graph execution, FAL, WebGL, video export.
 */
export function captureSubsystemError(error, subsystem, context = {}) {
  Sentry.withScope((scope) => {
    scope.setTag('subsystem', subsystem);
    for (const [key, value] of Object.entries(context)) {
      scope.setExtra(key, value);
    }
    Sentry.captureException(error);
  });
}
